import { useState } from "react"
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"
import { UserbotTracking as T } from "../../interfaces"
import {
  useDialogPassesQuery,
  useDialogsQuery,
  useJoinRecoveryQuery,
  useJoinRequestsQuery,
  useMembershipClarificationsQuery,
} from "../../store/trackingApi"
import {
  isTrackingUuid,
  TrackingListKind,
  trackingListRequest,
  trackingLists,
} from "../../store/trackingQuery"
import { listFields, safeField, TrackingRow, trackingNotes } from "./safeFields"
import { TrackingDetail } from "./trackingDetail"

export function TrackingList({ kind }: { kind: TrackingListKind }) {
  const config = trackingLists[kind]
  const [filters, setFilters] = useState<Record<string, string>>({})
  const [limit, setLimit] = useState("50")
  const [page, setPage] = useState(0)
  const [detail, setDetail] = useState<T.AccountResource | null>(null)
  const args = {
    ...filters,
    limit: Number(limit),
    offset: page * Number(limit),
  }
  let validation = ""
  try {
    trackingListRequest(kind, args)
  } catch (error) {
    validation = (error as Error).message
  }
  const requests = useJoinRequestsQuery(args as T.JoinRequestsQuery, {
    skip: kind !== "requests" || !!validation,
  })
  const recovery = useJoinRecoveryQuery(args as T.JoinRecoveryQuery, {
    skip: kind !== "recovery" || !!validation,
  })
  const dialogs = useDialogsQuery(args as T.DialogsQuery, {
    skip: kind !== "dialogs" || !!validation,
  })
  const passes = useDialogPassesQuery(args as T.DialogPassesQuery, {
    skip: kind !== "passes" || !!validation,
  })
  const clarifications = useMembershipClarificationsQuery(
    args as T.ClarificationsQuery,
    { skip: kind !== "clarifications" || !!validation },
  )
  const result = { requests, recovery, dialogs, passes, clarifications }[kind]
  const data = validation ? undefined : result.currentData
  const fields = listFields[kind]
  const hasDetails =
    kind === "requests" || kind === "recovery" || kind === "dialogs"
  const setFilter = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
    setPage(0)
  }
  const select = (
    key: string,
    label: string,
    values: readonly string[],
    defaultValue = "",
  ) => (
    <TextField
      key={key}
      select
      label={label}
      value={filters[key] ?? defaultValue}
      onChange={(event) => setFilter(key, event.target.value)}
      SelectProps={{ native: true }}
      InputLabelProps={{ shrink: true }}
      sx={{ minWidth: 170 }}
    >
      {!defaultValue && <option value="">Все</option>}
      {values.map((value) => (
        <option key={value} value={value}>
          {value}
        </option>
      ))}
    </TextField>
  )
  const openDetail = (row: TrackingRow) => {
    const accountId = "accountId" in row ? row.accountId : ""
    if (kind !== "recovery" && !isTrackingUuid(accountId)) return
    if (!isTrackingUuid(row.id)) return
    setDetail({ id: row.id, accountId })
  }
  return (
    <Stack spacing={2}>
      <Alert severity="info">{trackingNotes[kind]}</Alert>
      <Stack direction="row" gap={2} flexWrap="wrap">
        {Object.entries(config.filters).map(([key, values]) =>
          values.length ? (
            select(key, key, values)
          ) : (
            <TextField
              key={key}
              label={key}
              value={filters[key] ?? ""}
              onChange={(event) => setFilter(key, event.target.value)}
              helperText={
                key.endsWith("Id") ? "UUID" : "Код причины; не текст ошибки"
              }
              sx={{ minWidth: 220 }}
            />
          ),
        )}
        {select("sortBy", "sortBy", config.sorts, config.sorts[0])}
        {select("order", "order", ["DESC", "ASC"], "DESC")}
        <TextField
          label="from (ISO)"
          placeholder="2026-10-21T00:00:00Z"
          value={filters.from ?? ""}
          onChange={(event) => setFilter("from", event.target.value)}
        />
        <TextField
          label="until (ISO)"
          placeholder="2026-10-22T00:00:00Z"
          value={filters.until ?? ""}
          onChange={(event) => setFilter("until", event.target.value)}
        />
      </Stack>
      <Typography variant="caption">
        Включительные границы по {config.time}.{" "}
        {kind !== "recovery" &&
          "accountId — область данных, не персональное право доступа."}
      </Typography>
      {validation && <Alert severity="error">{validation}</Alert>}
      {!validation && result.isError && (
        <Alert severity="error">
          Ошибка загрузки. Проверьте фильтры и доступ; обновите вручную.
        </Alert>
      )}
      <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
        <TextField
          type="number"
          label="limit"
          value={limit}
          inputProps={{ min: 1, max: 500, step: 1 }}
          onChange={(event) => {
            setLimit(event.target.value)
            setPage(0)
          }}
          sx={{ width: 100 }}
        />
        <Button
          onClick={() => result.refetch()}
          disabled={!!validation || result.isFetching}
        >
          Обновить
        </Button>
        <Button
          aria-label="Предыдущая страница"
          disabled={page === 0 || !!validation || result.isFetching}
          onClick={() => setPage((value) => value - 1)}
        >
          Назад
        </Button>
        <Typography>
          Страница {page + 1} · Всего {data?.total ?? 0}
        </Typography>
        <Button
          aria-label="Следующая страница"
          disabled={
            !!validation ||
            result.isFetching ||
            !data ||
            (page + 1) * Number(limit) >= data.total
          }
          onClick={() => setPage((value) => value + 1)}
        >
          Далее
        </Button>
        {result.isFetching && !validation && <CircularProgress size={20} />}
      </Stack>
      <TableContainer
        component={Box}
        sx={{ maxHeight: "65vh", overflow: "auto" }}
      >
        <Table size="small" stickyHeader aria-label="Отслеживание">
          <TableHead>
            <TableRow>
              {fields.map((field) => (
                <TableCell key={field}>{field}</TableCell>
              ))}
              {hasDetails && <TableCell>Действия</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.items ?? []).map((row) => (
              <TableRow key={row.id}>
                {fields.map((field) => (
                  <TableCell
                    key={field}
                    sx={{
                      minWidth: 120,
                      maxWidth: 320,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {safeField(row, field)}
                  </TableCell>
                ))}
                {hasDetails && (
                  <TableCell>
                    <Button onClick={() => openDetail(row)}>Подробнее</Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!result.isFetching &&
          !result.isError &&
          !validation &&
          !data?.items.length && <Typography p={2}>Нет записей</Typography>}
      </TableContainer>
      {detail && hasDetails && (
        <TrackingDetail
          key={`${detail.accountId}:${detail.id}`}
          kind={kind}
          resource={detail}
          onClose={() => setDetail(null)}
        />
      )}
    </Stack>
  )
}
